import type { KnowledgeItem } from '@/types';

// 风电机组知识库（12 条）
// 覆盖：工艺基础 / 关键参数 / 三大故障处置 / 设备维护 / 高级操作
export const windturbineKnowledge: KnowledgeItem[] = [
  // ========== 工艺基础 ==========
  {
    id: 'WT-K001',
    tags: ['风电', '工艺', '基础'],
    question: '风电机组发电的基本原理是什么？',
    answer:
      '风能驱动三叶片叶轮旋转，叶轮通过主轴带动齿轮箱增速（通常 1:100 左右），' +
      '齿轮箱输出轴连接发电机转子切割磁感线产生交流电，再经变流器整流-逆变后并网。' +
      '兆瓦级风机叶轮直径 80~160m，塔筒高度 80~150m，切入风速 3m/s，切出风速 25m/s。',
  },
  {
    id: 'WT-K002',
    tags: ['桨距角', '关键参数'],
    question: '桨距角是什么？怎么控制功率？',
    answer:
      '桨距角是叶片迎风面与旋转平面的夹角，正常发电时 0~15°。桨距角 ↑ → 迎风面积 ↓ → 气动力矩 ↓ → 转速 ↓ → 功率 ↓。' +
      '风速超过额定风速（12m/s）后，主控通过变桨系统调节桨距角来维持额定功率。' +
      '紧急停机时三只叶片同时顺桨到 90°，完全脱离风能。',
  },
  {
    id: 'WT-K003',
    tags: ['偏航', '关键参数'],
    question: '偏航系统的作用是什么？',
    answer:
      '偏航系统驱动机舱绕塔筒中心旋转，使叶轮始终正对主风向。偏航角度正常 < 10°，' +
      '偏差每增加 1°，功率约按 cos(θ) 衰减 0.015%。偏航系统由偏航电机 + 偏航轴承 + 偏航制动器组成，' +
      '大型风机偏航速度 0.3~0.8°/s。',
  },
  {
    id: 'WT-K004',
    tags: ['齿轮箱', '关键参数'],
    question: '齿轮箱为什么需要温控？',
    answer:
      '齿轮箱把叶轮 10~20 rpm 低速转到发电机 1000~1800 rpm 高速，传动比大、齿轮啮合摩擦热多。' +
      '油温正常 50~80°C，超过 80°C 粘度下降 → 油膜变薄 → 齿面磨损/胶合。' +
      '通常配主冷却器 + 备用冷却风扇双重散热，还要定期化验润滑油酸值和水分。',
  },
  {
    id: 'WT-K005',
    tags: ['变流器', '关键参数'],
    question: '变流器在风机里起什么作用？',
    answer:
      '风机发电机输出电压和频率随风速变化，无法直接并网。变流器先把交流整流为直流（整流侧），' +
      '再把直流逆变为 50Hz/690V 工频交流（逆变侧）送入电网。' +
      '同时具备低电压穿越、无功补偿、谐波抑制等电网支撑功能。',
  },

  // ========== 典型故障处置 ==========
  {
    id: 'WT-K101',
    tags: ['齿轮箱', '过热', '故障'],
    question: '齿轮箱油温升到 95°C 怎么办？',
    answer:
      '这是齿轮箱过热故障。正确操作：①观察温度趋势 15 秒 ②降有功功率从 2.5 降到 1.5 MW（减热源）' +
      '③启动备用冷却风扇 ④等 60 秒观察油温下降 ⑤确认 T-GB < 80°C 通关。' +
      '⚠️ 绝对不能：不降温直接启冷却 —— 温差大导致齿面热裂。',
  },
  {
    id: 'WT-K102',
    tags: ['桨距', '不对称', '极危故障'],
    question: '三只叶片桨距角不一致是什么故障？怎么处理？',
    answer:
      '⚠️ 极危故障"桨距角不对称"——叶轮严重不平衡，塔筒振动急剧上升。正确操作：' +
      '①观察三只叶片桨距角差异 ②紧急降转速到 5 rpm ③同步三只叶片到统一角度 ④等 30 秒观察振动回落 ' +
      '⑤确认 VIB-TOWER < 2.5 mm/s 通关。⚠️ 绝对不能：不降速直接调桨距 —— 叶轮负载突变可能断裂。',
  },
  {
    id: 'WT-K103',
    tags: ['偏航', '锁定', '故障'],
    question: '偏航没对准风向，功率大幅下降怎么办？',
    answer:
      '这是偏航锁定失效故障。正确操作：①观察偏航角度与风向偏差 ' +
      '②释放偏航制动器 ③手动偏航对风到风向 ±5° 以内 ④重新锁定偏航制动器 ' +
      '⑤等 40 秒观察功率回升 ⑥确认 POWER > 2.0 MW 通关。' +
      '⚠️ 绝对不能：未解刹就强制偏航 —— 电机烧毁。',
  },
  {
    id: 'WT-K104',
    tags: ['振动', '监测', '故障'],
    question: '风机振动超标怎么排查？',
    answer:
      '振动超标分三级排查：①看转速和桨距 —— 转速高 + 桨距不平衡是叶轮问题 ' +
      '②看齿轮箱油温 —— 油温高导致润滑恶化 → 振动升 ' +
      '③看偏航角度 —— 偏航不对风导致气动不平衡。' +
      '塔筒振动正常 < 2.5 mm/s，轮毂振动 < 4.5 mm/s，齿轮箱振动 < 4.5 mm/s。',
  },

  // ========== 设备维护 ==========
  {
    id: 'WT-K201',
    tags: ['叶片', '维护'],
    question: '风机叶片怎么维护？',
    answer:
      '叶片维护重点：①表面检查 —— 雷击损伤、前缘腐蚀、后缘开裂 ' +
      '②螺栓力矩 —— 叶根螺栓每年抽检 10%，M36 螺栓力矩通常 1800~2200 Nm ' +
      '③变桨轴承润滑 —— 每 6 个月注脂，防止轴承卡滞导致桨距不对称 ' +
      '④防雷系统 —— 接闪器和引下线电阻 < 10mΩ。',
  },
  {
    id: 'WT-K202',
    tags: ['发电机', '维护'],
    question: '发电机绕组温度高怎么处理？',
    answer:
      '绕组温度正常 < 120°C。温度高时：①先降有功功率 10~20% ' +
      '②检查冷却风扇运行电流和风向 ③检查空冷器散热片是否堵塞 ' +
      '④如果温度持续 > 130°C 必须停机，等自然冷却到 80°C 以下再启机。',
  },

  // ========== 高级操作 ==========
  {
    id: 'WT-K301',
    tags: ['低电压穿越', '电网', '高级'],
    question: '什么是"低电压穿越"？',
    answer:
      '电网电压瞬间跌落时（如短路故障），风机不能立即脱网，必须继续支撑电网 0.625~3 秒。' +
      '这期间变流器通过 Crowbar 或 Chopper 消耗多余能量，维持发电机励磁。' +
      '低电压穿越能力是电网准入的核心指标，脱网会导致电网崩溃扩大。',
  },
];
